/**
 * Sistema de logging centralizado
 * Controla cuándo se muestran logs según el entorno y configuración
 */

// Determinar si estamos en desarrollo
const isDevelopment = import.meta.env.DEV;

// Variable de control para habilitar/deshabilitar logs manualmente
// Puedes cambiarla dinámicamente desde la consola: window.enableLogs = true/false
declare global {
  interface Window {
    enableLogs?: boolean;
    __FORCE_DISABLE_LOGS__?: boolean;
  }
}

// Por defecto, los logs están habilitados en desarrollo
if (typeof window !== 'undefined') {
  window.enableLogs = isDevelopment;
}

/**
 * Verifica si los logs deben mostrarse
 */
function shouldLog(): boolean {
  // Si hay un flag de forzar deshabilitación, respetarlo
  if (typeof window !== 'undefined' && window.__FORCE_DISABLE_LOGS__) {
    return false;
  }
  
  // En desarrollo, siempre permitir (a menos que se deshabilite manualmente)
  if (isDevelopment) {
    return window.enableLogs !== false;
  }
  
  // En producción, solo si está explícitamente habilitado
  return typeof window !== 'undefined' && window.enableLogs === true;
}

/**
 * Logger condicional - solo muestra logs si está habilitado
 */
export const logger = {
  log: (...args: any[]) => {
    if (shouldLog()) {
      console.log(...args);
    }
  },
  
  error: (...args: any[]) => {
    // Los errores siempre se muestran (son críticos)
    console.error(...args);
  },
  
  warn: (...args: any[]) => {
    if (shouldLog()) {
      console.warn(...args);
    }
  },
  
  info: (...args: any[]) => {
    if (shouldLog()) {
      console.info(...args);
    }
  },
  
  debug: (...args: any[]) => {
    // Debug solo en desarrollo
    if (isDevelopment && shouldLog()) {
      console.debug(...args);
    }
  },
  
  // Método especial para forzar logs (incluso en producción)
  force: (...args: any[]) => {
    console.log('[FORCED]', ...args);
  }
};

/**
 * Funciones de utilidad para controlar logs
 */
export const loggerControl = {
  /**
   * Habilitar logs manualmente
   */
  enable: () => {
    if (typeof window !== 'undefined') {
      window.enableLogs = true;
      window.__FORCE_DISABLE_LOGS__ = false;
      console.log('✅ Logs habilitados');
    }
  },
  
  /**
   * Deshabilitar logs manualmente
   */
  disable: () => {
    if (typeof window !== 'undefined') {
      window.enableLogs = false;
      console.log('❌ Logs deshabilitados');
    }
  },
  
  /**
   * Forzar deshabilitación total (incluso errores no críticos)
   */
  forceDisable: () => {
    if (typeof window !== 'undefined') {
      window.__FORCE_DISABLE_LOGS__ = true;
      window.enableLogs = false;
      console.log('🔒 Logs completamente deshabilitados');
    }
  },
  
  /**
   * Verificar estado actual
   */
  status: () => {
    const enabled = shouldLog();
    const mode = isDevelopment ? 'desarrollo' : 'producción';
    console.log(`📊 Estado de logs: ${enabled ? '✅ Habilitado' : '❌ Deshabilitado'} (Modo: ${mode})`);
  }
};

// Exponer controles en window para uso desde consola
if (typeof window !== 'undefined') {
  (window as any).loggerControl = loggerControl;
}
